// What an invoice prints besides its lines: the logo and the bank details.
// Chosen the way the database chooses them (ledger._printed_bank): the
// design's bank account if it names one, otherwise the default account.
import { useEffect, useState } from 'react';
import { formatIban, formatSortCode } from '@elysian/ui';
import type { BankAccountSummary, BankDetails, TemplateSettings } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';

export type PrintedBank = BankDetails & { code: string };

export function printedBank(accounts: BankAccountSummary[], code?: string | null): PrintedBank | null {
  const a = code ? accounts.find((x) => x.code === code) : accounts.find((x) => x.details?.is_default);
  return a?.details ? { code: a.code, ...a.details } : null;
}

/** One line for the invoice footer: name, then UK numbers, then IBAN and BIC. */
export function bankLine(b: BankDetails): string {
  const parts = [b.account_name];
  if (b.sort_code && b.account_number) parts.push(`Sort code ${formatSortCode(b.sort_code)}`, `Account ${b.account_number}`);
  if (b.iban) parts.push(`IBAN ${formatIban(b.iban)}`);
  if (b.iban && b.bic) parts.push(`BIC ${b.bic}`);
  return parts.join(' · ');
}

/** An object URL for a stored logo, released when it changes or the screen closes. */
export function useLogoUrl(assetId: string | null | undefined): string | null {
  const { source } = useSession();
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    let made: string | null = null;
    setUrl(null);
    if (assetId) {
      source.brandAssetFile(assetId).then((blob) => {
        if (!alive || !blob) return;
        made = URL.createObjectURL(blob);
        setUrl(made);
      }).catch(() => undefined);
    }
    return () => { alive = false; if (made) URL.revokeObjectURL(made); };
  }, [source, assetId]);
  return url;
}

/** The bank accounts (with their details) and the logo for a design. */
export function usePrintExtras(settings: TemplateSettings, reloadKey: unknown = 0) {
  const { source } = useSession();
  const accounts = useAsync(() => source.bankAccounts(), [source, reloadKey], { keep: true });
  const list = accounts.state === 'ready' ? accounts.data : [];
  return {
    accounts: list,
    bank: printedBank(list, settings.bank_account_code),
    logoUrl: useLogoUrl(settings.brand?.logo_asset_id),
  };
}
